import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { db, storage } from './firebase';
import { doc, getDoc, setDoc, collection, getDocs, writeBatch, onSnapshot, updateDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  DndContext, DragOverlay, MouseSensor, TouchSensor, useSensor, useSensors,
  useDroppable, useDraggable, pointerWithin, closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, arrayMove, rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import PersonConfigModal from './PersonConfigModal';
import downscaleImage from './downscaleImage';
import { splitTile, tileHasInlinePrivateValue } from './tilePrivacy';
import { getConnection, getConnections, getConnectionsFull, sendConnect } from './connections';
import { isBirthdayToday } from './birthday';
import { platformLabel as getPlatformLabel, detectPlatform, buildSocialUrl, SUPPORTED_PLATFORM_NAMES } from './platforms';
import { createInvite, inviteUrl, shareInvite } from './invites';

const OWNER_ACCOUNTS = ['corey'];

const LAYOUTS = [
  { key: 'list',  label: 'List' },
  { key: 'grid2', label: '2x4' },
  { key: 'grid4', label: '4x2' },
];
const DEFAULT_LAYOUT = 'grid4';

function genId() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function isURL(str) {
  return str.startsWith('http://') || str.startsWith('https://') || str.includes('.');
}

function isPhoneNumber(str) {
  const digits = str.replace(/[\s\-().+]/g, '');
  return digits.length >= 7 && digits.length <= 15 && /^\d+$/.test(digits);
}

function normalizePhone(str) {
  const hasPlus = str.trimStart().startsWith('+');
  const digits = str.replace(/\D/g, '');
  return hasPlus ? `+${digits}` : digits;
}


function getActionLabel(entry) {
  if (!entry) return '';
  if (entry.primaryAction === 'TEXT') return 'Text';
  if (entry.primaryAction === 'SOCIAL') return getPlatformLabel(entry.primaryValue || '');
  return entry.yt8Username ? `@${entry.yt8Username}` : 'Profile';
}

// Badges (connected / verified / pending) for a tile, using the live enrichment
// map when present so the editor matches the public profile.
function TileBadges({ entry, en }) {
  const isOwner = OWNER_ACCOUNTS.includes(entry.yt8Username);
  const status = en ? en.status : entry.connectionStatus;
  const verified = (en ? en.verified : entry.verified) || isOwner;
  const connected = status === 'accepted' || isOwner;
  const pending = !connected && status === 'pending';
  return (
    <>
      {connected && <img src="/connected.png" alt="Connected" title="Connected on Your Top 8" className={`tile-badge-8-img${verified ? ' tile-badge-8-img--paired' : ''}`} />}
      {verified && <img src="/verified.png" alt="Verified" title="Verified" className="tile-badge-8-img" />}
      {pending && (
        <span className="tile-badge-8" style={{ background: 'var(--muted)', color: 'var(--bg)' }} title="Connection pending">?</span>
      )}
    </>
  );
}

// The visual content of a tile. Shared by the in-grid sortable tile and the
// floating drag overlay so they look identical.
function TileCard({ entry, rank, en, overlay }) {
  const isBday = isBirthdayToday(en?.birthday);
  return (
    <>
      <div className="tile-img" style={{ position: 'relative' }}>
        {entry.photoURL
          ? <img src={entry.photoURL} alt={entry.displayName} draggable={false} />
          : <div style={{
              width: '100%', height: '100%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 'clamp(28px,6vw,48px)', fontWeight: 800, color: 'var(--accent)',
              background: 'var(--card-2)',
            }}>
              {entry.displayName?.[0]?.toUpperCase() || '?'}
            </div>
        }
        {!overlay && <div className="tile-edit-overlay" />}
        {rank != null && <span className="rank-badge">{rank}</span>}
        <TileBadges entry={entry} en={en} />
        {isBday && (
          <div style={{
            position: 'absolute', inset: 0, borderRadius: 'inherit',
            border: '3px solid #FFC83D', boxShadow: 'inset 0 0 10px rgba(255,200,61,0.55)',
            pointerEvents: 'none', zIndex: 3,
          }} />
        )}
      </div>
      <div className="tile-body">
        <div className="tile-name">{entry.displayName}</div>
        <div className="tile-link">{getActionLabel(entry)}</div>
      </div>
    </>
  );
}

// A draggable, sortable tile.
//   quick tap/click         -> fire the tile's primary action (onOpen)
//   long-press then drag     -> rearrange (single gesture, via dnd-kit)
//   long-press then release  -> edit (touch); the pencil also edits (any device)
function SortableTile({ entry, rank, en, onOpen, onEdit, onInvite, justDraggedRef, dropTarget, leaving }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: entry.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    touchAction: 'manipulation',
  };
  const stop = e => e.stopPropagation();
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`tile${dropTarget ? ' tile--drop-target' : ''}${leaving ? ' tile--leaving' : ''}`}
      {...attributes}
      {...listeners}
      onClick={() => {
        // Suppress the click that fires right after a drag ends.
        if (Date.now() - justDraggedRef.current < 250) return;
        onOpen(entry);
      }}
      onContextMenu={e => e.preventDefault()}
      title={getActionLabel(entry)}
    >
      <TileCard entry={entry} rank={rank} en={en} />
      {!entry.isRegisteredUser && entry.displayName && (
        <button
          type="button"
          className="tile-invite-btn"
          onPointerDown={stop}
          onMouseDown={stop}
          onTouchStart={stop}
          onClick={e => { e.stopPropagation(); onInvite(entry); }}
          title={`Invite ${entry.displayName} to Your Top 8`}
          aria-label={`Invite ${entry.displayName} to Your Top 8`}
        >✉</button>
      )}
      <button
        type="button"
        className="tile-edit-btn"
        onPointerDown={stop}
        onMouseDown={stop}
        onTouchStart={stop}
        onClick={e => { e.stopPropagation(); onEdit(entry); }}
        title="Edit this tile"
        aria-label="Edit this tile"
      >✎</button>
    </div>
  );
}

// Red removal bar pinned to a screen edge. Dropping a tile on it removes it.
// Always mounted (so dnd-kit can measure it); only shown while dragging.
function DeleteZone({ side, visible }) {
  const { setNodeRef, isOver } = useDroppable({ id: `delete-${side}` });
  return (
    <div ref={setNodeRef} className={`delete-zone ${side}${visible ? ' show' : ''}${isOver && visible ? ' active' : ''}`}>
      Remove
    </div>
  );
}

// A drawer chip (a Connection or a Prospect). Long-press (touch) or click-drag
// (desktop) to drag it up into a Top 8 slot; a plain tap fires onTap (open a
// prospect to invite, or view a connection's profile).
function DraggableChip({ id, name, photoURL, title, onTap, justDraggedRef }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id });
  return (
    <div
      ref={setNodeRef}
      className="conn-chip"
      style={{ opacity: isDragging ? 0.4 : 1, touchAction: 'manipulation' }}
      title={title}
      {...attributes}
      {...listeners}
      onClick={() => {
        if (Date.now() - justDraggedRef.current < 250) return; // swallow post-drag click
        onTap?.();
      }}
    >
      <div className="conn-chip-avatar">
        {photoURL ? <img src={photoURL} alt="" draggable={false} /> : (name?.[0]?.toUpperCase() || '?')}
      </div>
      <div className="conn-chip-name">{name}</div>
    </div>
  );
}

// An empty Top 8 slot. Droppable so a dragged connection can land here when the
// Top 8 isn't full; clicking focuses the add bar.
function EmptySlot({ id, onFocus }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`tile placeholder${isOver ? ' placeholder--over' : ''}`}
      onClick={onFocus}
    >
      <div className="tile-img">
        <div className="plus" aria-hidden="true">+</div>
      </div>
      <div className="tile-body">
        <div className="tile-name">Add Friend</div>
        <div className="tile-link">Enter username below</div>
      </div>
    </div>
  );
}

// Migrate old single-action entries to new multi-connection format
function migrateEntry(entry) {
  if (entry.connections) return entry;
  const connValue = entry.actionValue || entry.id || '';
  const conn = { id: genId(), type: entry.actionType || 'PROFILE', value: connValue };
  return {
    id: genId(),
    displayName: entry.displayName || '',
    photoURL: entry.photoURL || null,
    isRegisteredUser: entry.isRegisteredUser || false,
    yt8Username: entry.isRegisteredUser ? (entry.actionValue || entry.id) : null,
    primaryAction: entry.actionType || 'PROFILE',
    primaryValue: connValue,
    connections: [conn],
  };
}

function Top8Editor({ currentUser, currentUserProfile, embedded = false, onDone, coachAdd = false }) {
  const navigate = useNavigate();
  const [top8, setTop8]         = useState([]);
  const [newInput, setNewInput] = useState('');
  const [adding, setAdding]     = useState(false);
  const [saving, setSaving]     = useState(false);
  const [addError, setAddError] = useState('');
  const [configEntry, setConfigEntry] = useState(null);
  const [saveStatus, setSaveStatus]   = useState('');
  const [autoSave, setAutoSave]       = useState(true);
  const [hasUnsaved, setHasUnsaved]   = useState(false);
  const [conflict, setConflict]       = useState(false);
  // Live verified + connection status per tile id, so the editor's badges match
  // exactly what visitors see on the public profile (display-only, not persisted).
  const [enrichMap, setEnrichMap]     = useState({});
  const loadedRef = useRef(false);
  const saveTimerRef = useRef(null);
  const pendingSaveRef = useRef(false);
  const savedIdsRef = useRef([]);
  const hasUnsavedRef = useRef(false);

  // Pending entry (name/photo modal for social/phone adds)
  const [pendingEntry, setPendingEntry]               = useState(null);
  const [pendingName, setPendingName]                 = useState('');
  const [pendingPhoto, setPendingPhoto]               = useState(null);
  const [pendingPhotoPreview, setPendingPhotoPreview] = useState(null);
  const [pendingUploading, setPendingUploading]       = useState(false);
  const pendingPhotoRef = useRef();

  // Drag state. activeId is the tile being dragged (drives the floating overlay
  // and shows the delete bars). justDraggedRef stamps when a drag ended so the
  // click that fires right after doesn't also open the editor.
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null); // current drop target, for the hover indicator
  const [confirmRemove, setConfirmRemove] = useState(null); // tile pending "are you sure"
  const justDraggedRef = useRef(0);
  const inputRef = useRef();

  // Drawer: Connections (accepted YT8 users) + Prospects (people you added by
  // phone/social who haven't joined yet). Both draggable into a Top 8 slot.
  const [connections, setConnections] = useState([]);
  const [pending, setPending] = useState([]);        // outgoing requests not yet accepted
  const [prospects, setProspects] = useState([]);   // owner-only bench, full tiles
  const [drawerTab, setDrawerTab] = useState('connections'); // 'connections' | 'pending' | 'prospects'
  const [connSearch, setConnSearch] = useState('');
  const [allUsers, setAllUsers] = useState(null);   // lazily loaded for name search

  // Owner-chosen Top 8 layout (saved to their profile; everyone sees it).
  const [layout, setLayout] = useState(currentUserProfile?.top8Layout || DEFAULT_LAYOUT);
  useEffect(() => {
    if (currentUserProfile?.top8Layout) setLayout(currentUserProfile.top8Layout);
  }, [currentUserProfile?.top8Layout]);
  const changeLayout = (next) => {
    setLayout(next);
    if (currentUser) updateDoc(doc(db, 'users', currentUser.uid), { top8Layout: next }).catch(() => {});
  };

  // Load accepted connections + outgoing-pending requests for the drawer.
  useEffect(() => {
    if (!currentUser) return;
    let active = true;
    const resolve = (uid) => getDoc(doc(db, 'users', uid)).then(s => {
      if (!s.exists()) return null;
      const d = s.data();
      return { uid, username: d.username, displayName: d.displayName || d.username, photoURL: d.photoURL || null };
    }).catch(() => null);
    (async () => {
      try {
        const full = await getConnectionsFull(currentUser.uid);
        const acceptedUids = full.filter(c => c.status === 'accepted').map(c => c.uid);
        const pendingUids = full.filter(c => c.status === 'pending' && c.requestedBy === currentUser.uid).map(c => c.uid);
        const [acc, pend] = await Promise.all([
          Promise.all(acceptedUids.map(resolve)),
          Promise.all(pendingUids.map(resolve)),
        ]);
        if (active) { setConnections(acc.filter(Boolean)); setPending(pend.filter(Boolean)); }
      } catch { /* ignore */ }
    })();
    return () => { active = false; };
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist the Prospects bench (owner-only) when it changes, debounced.
  useEffect(() => {
    if (!loadedRef.current || !currentUser) return;
    const t = setTimeout(() => {
      setDoc(doc(db, 'users', currentUser.uid, 'private', 'roster'), { prospects }).catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, [prospects]); // eslint-disable-line react-hooks/exhaustive-deps

  const sensors = useSensors(
    // Desktop: a tiny move starts the drag immediately (no hold). A plain click
    // stays a click and fires the tile's action.
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    // Touch: a press of ~250ms lifts the tile so you can drag it in one motion;
    // a quick tap fires the action, and a normal swipe still scrolls.
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
  );

  // Prefer a delete bar when the pointer is over one; otherwise sort normally.
  const collisionDetection = useCallback((args) => {
    const id = args.active?.id ? String(args.active.id) : '';
    const isChip = id.startsWith('conn:') || id.startsWith('prospect:');
    const hits = pointerWithin(args);
    if (isChip) {
      // A drawer chip only targets a tile/slot the pointer is actually over
      // (never the delete bars), so releasing off the grid does nothing instead
      // of snapping to the nearest tile.
      return hits.filter(h => !String(h.id).startsWith('delete'));
    }
    const del = hits.find(h => String(h.id).startsWith('delete'));
    return del ? [del] : closestCenter(args);
  }, []);

  const setStatus = (msg, good = false) => {
    setSaveStatus({ msg, good });
    if (msg) setTimeout(() => setSaveStatus(''), 1800);
  };

  // One-tap invite straight from a prospect tile (no digging into the edit panel).
  const handleInvite = async (entry) => {
    if (!currentUserProfile || !currentUser) return;
    try {
      const token = await createInvite(entry, {
        uid: currentUser.uid,
        username: currentUserProfile.username,
        displayName: currentUserProfile.displayName,
        photoURL: currentUserProfile.photoURL,
      });
      const res = await shareInvite(inviteUrl(token), currentUserProfile.displayName || 'Someone');
      setStatus(res === 'copied' ? 'Invite link copied' : 'Invite shared', true);
    } catch (e) {
      console.error('Invite failed:', e);
      setStatus('Could not create invite', false);
    }
  };

  // Persist: write the sanitized public top8 + each tile's private action doc,
  // and delete private docs for any removed tiles. The public document never
  // receives phone numbers or third-party URLs (see tilePrivacy.js).
  const persist = async (tiles, removedIds = []) => {
    const batch = writeBatch(db);
    const publicTiles = tiles.map(t => {
      const { publicTile, privateAction } = splitTile(t);
      batch.set(doc(db, 'users', currentUser.uid, 'privateTileActions', t.id), privateAction);
      return publicTile;
    });
    // top8Usernames powers the "Top 8 can see this" social visibility rule.
    const top8Usernames = publicTiles.filter(t => t.yt8Username).map(t => t.yt8Username);
    batch.update(doc(db, 'users', currentUser.uid), { top8: publicTiles, top8Usernames });
    removedIds.forEach(id =>
      batch.delete(doc(db, 'users', currentUser.uid, 'privateTileActions', id))
    );
    await batch.commit();
    // Track what we last wrote so the realtime listener can ignore our own saves.
    savedIdsRef.current = tiles.map(t => t.id);
  };

  // Load: merge the owner's private action values back onto the public tiles so
  // the editor shows the real targets. Migrate legacy/inline data on the way in.
  const loadTiles = useCallback(async () => {
    if (!currentUser) return;
    {
      const snap = await getDoc(doc(db, 'users', currentUser.uid));
      if (!snap.exists()) return;
      const publicTop8 = snap.data().top8 || [];

      const privSnap = await getDocs(collection(db, 'users', currentUser.uid, 'privateTileActions'));
      const privMap = {};
      privSnap.forEach(d => { privMap[d.id] = d.data(); });

      // Build full editable tiles. Tiles with a private doc keep their stable id
      // and have their real values restored. Only tiles with NO private doc are
      // treated as legacy and run through migrateEntry (which may reshape them).
      const fullTiles = publicTop8.map(pub => {
        const priv = privMap[pub.id];
        if (priv) {
          return {
            ...pub,
            primaryAction: priv.primaryAction || pub.primaryAction,
            primaryValue: priv.primaryValue ?? pub.primaryValue,
            connections: priv.connections || pub.connections || [],
          };
        }
        return migrateEntry(pub);
      });

      // Defensive: every tile needs a unique, non-empty id. dnd-kit sorts by id,
      // so a falsy or duplicate id would break drag-to-reorder.
      const seenIds = new Set();
      let idsFixed = false;
      const tiles = fullTiles.map(t => {
        let id = t.id;
        if (!id || seenIds.has(id)) { id = genId(); idsFixed = true; }
        seenIds.add(id);
        return id === t.id ? t : { ...t, id };
      });

      setTop8(tiles);
      savedIdsRef.current = tiles.map(t => t.id);

      // Load the Prospects bench (owner-only). Drop any that have since joined
      // the Top 8, so the same person can't sit in both places.
      try {
        const rosterSnap = await getDoc(doc(db, 'users', currentUser.uid, 'private', 'roster'));
        const bench = rosterSnap.exists() ? (rosterSnap.data().prospects || []) : [];
        const inTop8 = new Set(tiles.map(t => t.id));
        setProspects(bench.filter(p => p && p.id && !inTop8.has(p.id)));
      } catch { setProspects([]); }

      loadedRef.current = true;
      setHasUnsaved(false);
      setConflict(false);

      // Enrich registered tiles with the LIVE verified flag and connection status
      // (the same sources the public profile reads), so /me and /:username never
      // disagree. Stored connectionStatus can be stale after a request is accepted.
      try {
        const connMap = await getConnections(currentUser.uid);
        const pairs = await Promise.all(tiles.map(async t => {
          if (!t.yt8Username) return [t.id, null];
          const isOwner = OWNER_ACCOUNTS.includes(t.yt8Username);
          let verified = isOwner, status = t.connectionStatus || null, birthday = null;
          try {
            const uSnap = await getDoc(doc(db, 'usernames', t.yt8Username));
            if (uSnap.exists()) {
              const eUid = uSnap.data().uid;
              if (connMap[eUid]) status = connMap[eUid];
              const eData = await getDoc(doc(db, 'users', eUid));
              if (eData.exists()) { verified = verified || !!eData.data().verified; birthday = eData.data().birthday || null; }
            }
          } catch { /* keep fallbacks */ }
          return [t.id, { verified, status, birthday }];
        }));
        const map = {};
        pairs.forEach(([id, v]) => { if (v) map[id] = v; });
        setEnrichMap(map);
      } catch { /* badges fall back to stored values */ }

      // Re-persist (sanitize + write private docs) if anything is unmigrated.
      const needsMigration =
        idsFixed ||
        publicTop8.some(t => !t.connections) ||
        tiles.some(tileHasInlinePrivateValue) ||
        tiles.some(t => !privMap[t.id]) ||
        // Self-heal malformed tiles flagged public without a real YT8 username.
        publicTop8.some(t => t.publicActionAvailable && !t.yt8Username);
      if (needsMigration) {
        try { await persist(tiles, []); } catch (e) { console.error('Migration failed:', e); }
      }
    }
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadedRef.current = false; loadTiles(); }, [loadTiles]);

  // Keep the editor synced across devices without polling. Reacts only to
  // external changes (not our own saves), and never clobbers unsaved edits.
  useEffect(() => { hasUnsavedRef.current = hasUnsaved; }, [hasUnsaved]);
  useEffect(() => {
    if (!currentUser) return;
    const unsub = onSnapshot(doc(db, 'users', currentUser.uid), (snap) => {
      if (!snap.exists() || !loadedRef.current) return;
      // A debounced auto-save is about to overwrite the doc with our local state.
      // Don't reload (which would clobber the in-progress edit) until it lands.
      if (pendingSaveRef.current) return;
      const remoteIds = (snap.data().top8 || []).map(t => t.id).join(',');
      if (remoteIds === savedIdsRef.current.join(',')) return; // our own save
      if (hasUnsavedRef.current) setConflict(true);
      else loadTiles();
    });
    return unsub;
  }, [currentUser, loadTiles]);

  const saveNow = async () => {
    if (!currentUser) return;
    setSaving(true);
    try {
      const currentIds = top8.map(t => t.id);
      const removed = savedIdsRef.current.filter(id => !currentIds.includes(id));
      await persist(top8, removed);
      setHasUnsaved(false);
      setStatus('Saved', true);
    } catch {
      setStatus('Save failed', false);
    }
    setSaving(false);
  };

  const toggleAutoSave = () => {
    // Turning auto-save back on flushes any held changes in one write (one
    // notification per affected person instead of a flood).
    if (!autoSave && hasUnsaved) saveNow();
    setAutoSave(v => !v);
  };

  // Auto-save with debounce (held when auto-save is off)
  useEffect(() => {
    if (!loadedRef.current || !currentUser) return;
    if (!autoSave) { pendingSaveRef.current = false; setHasUnsaved(true); return; }
    clearTimeout(saveTimerRef.current);
    // While a save is queued, our local tiles are the source of truth, so the
    // realtime listener must not reload and clobber the edit before we write it.
    pendingSaveRef.current = true;
    setSaveStatus({ msg: 'Saving…', good: false });
    saveTimerRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        const currentIds = top8.map(t => t.id);
        const removed = savedIdsRef.current.filter(id => !currentIds.includes(id));
        await persist(top8, removed);
        setHasUnsaved(false);
        setStatus('Saved', true);
      } catch {
        setStatus('Save failed', false);
      } finally {
        pendingSaveRef.current = false;
      }
      setSaving(false);
    }, 800);
    return () => clearTimeout(saveTimerRef.current);
  }, [top8, autoSave]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tap target: do the thing the tile is for.
  const firePrimaryAction = (entry) => {
    if (!entry) return;
    if (entry.primaryAction === 'TEXT' && entry.primaryValue) {
      window.location.href = `sms:${entry.primaryValue}`;
    } else if (entry.primaryAction === 'SOCIAL' && entry.primaryValue) {
      window.open(entry.primaryValue, '_blank', 'noopener');
    } else if (entry.yt8Username) {
      navigate(`/${entry.yt8Username}`);
    } else {
      setConfigEntry(entry); // nothing to fire; fall back to edit
    }
  };

  // Open a SPECIFIC saved link for a person (not just their primary). Used from
  // the edit panel so you can reach someone's other links (their profile, a
  // social, a text) even when their primary action is something else.
  const openConnection = (conn) => {
    if (!conn || !conn.value) return;
    if (conn.type === 'TEXT') window.location.href = `sms:${conn.value}`;
    else if (conn.type === 'SOCIAL') window.open(conn.value, '_blank', 'noopener');
    else if (conn.type === 'PROFILE') navigate(`/${conn.value}`);
  };

  // ── Drag to reorder / remove (dnd-kit) ───────────────────────────────────────
  const handleDragStart = ({ active }) => setActiveId(active.id);
  const handleDragOver = ({ over }) => setOverId(over?.id ?? null);
  const handleDragCancel = () => { setActiveId(null); setOverId(null); };

  const handleDragEnd = ({ active, over, activatorEvent }) => {
    setOverId(null);
    setActiveId(null);
    justDraggedRef.current = Date.now(); // swallow the trailing click

    // Dragging someone from the drawer (a Connection or a Prospect) into the Top 8.
    if (String(active.id).startsWith('conn:') || String(active.id).startsWith('prospect:')) {
      if (!over || String(over.id).startsWith('delete')) return;

      let tile = null;             // the tile to place into the Top 8
      let removeProspectId = null; // a promoted prospect, to drop from the bench
      if (String(active.id).startsWith('conn:')) {
        const uid = String(active.id).slice('conn:'.length);
        const isPending = pending.some(c => c.uid === uid);
        const conn = connections.find(c => c.uid === uid) || pending.find(c => c.uid === uid);
        if (!conn) return;
        if (top8.some(t => t.yt8Username === conn.username)) return; // already in
        tile = {
          id: genId(),
          displayName: conn.displayName,
          photoURL: conn.photoURL,
          isRegisteredUser: true,
          yt8Username: conn.username,
          primaryAction: 'PROFILE',
          primaryValue: conn.username,
          connections: [{ id: genId(), type: 'PROFILE', value: conn.username }],
          connectionStatus: isPending ? 'pending' : 'accepted',
        };
      } else {
        const pid = String(active.id).slice('prospect:'.length);
        const p = prospects.find(x => x.id === pid);
        if (!p) return;
        tile = p;
        removeProspectId = pid;
      }

      const overIdx = top8.findIndex(t => t.id === over.id);
      let displaced = null;
      if (overIdx !== -1) {
        // Slot in at the target position and shift the rest down. If that pushes
        // past 8, the last spot drops off (like rearranging, not a blind replace).
        const next = [...top8];
        next.splice(overIdx, 0, tile);
        if (next.length > 8) displaced = next.pop();
        setTop8(next);
      } else if (top8.length < 8) {
        setTop8(prev => [...prev, tile]); // dropped on an empty slot
      } else {
        return; // full and not over a tile
      }

      // Bench update: drop a promoted prospect; bench a displaced unverified
      // person (a phone/social tile with no real account) so they aren't lost.
      setProspects(prev => {
        let next = prev;
        if (removeProspectId) next = next.filter(p => p.id !== removeProspectId);
        if (displaced && !displaced.yt8Username && !next.some(p => p.id === displaced.id)) {
          next = [...next, displaced];
        }
        return next;
      });
      setConnSearch('');
      return;
    }

    if (!over) return;
    if (String(over.id).startsWith('delete')) {
      const entry = top8.find(t => t.id === active.id);
      if (entry) setConfirmRemove(entry); // confirm before removing
      return;
    }
    if (over.id !== active.id) {
      setTop8(prev => {
        const from = prev.findIndex(t => t.id === active.id);
        const to   = prev.findIndex(t => t.id === over.id);
        if (from === -1 || to === -1) return prev;
        return arrayMove(prev, from, to);
      });
      return;
    }
    // Lifted and released in place via a touch long-press = edit this tile.
    const byTouch = typeof TouchEvent !== 'undefined' && activatorEvent instanceof TouchEvent;
    if (byTouch) {
      const entry = top8.find(t => t.id === active.id);
      if (entry) setConfigEntry(entry);
    }
  };

  const activeEntry = activeId ? top8.find(t => t.id === activeId) : null;
  // A drawer chip (Connection or Prospect) being dragged, for the overlay.
  let activeChip = null;
  if (activeId && String(activeId).startsWith('conn:')) {
    const uid = String(activeId).slice('conn:'.length);
    const c = connections.find(x => x.uid === uid) || pending.find(x => x.uid === uid);
    if (c) activeChip = { displayName: c.displayName, photoURL: c.photoURL };
  } else if (activeId && String(activeId).startsWith('prospect:')) {
    const p = prospects.find(x => x.id === String(activeId).slice('prospect:'.length));
    if (p) activeChip = { displayName: p.displayName, photoURL: p.photoURL };
  }

  // ── Add person ───────────────────────────────────────────────────────────────

  // Lazily load all users once, so the add bar can find people by name (not just
  // exact username). Fine for the current scale; would need a search index later.
  const ensureAllUsers = async () => {
    if (allUsers !== null) return;
    setAllUsers([]); // mark as loading so we don't fetch twice
    try {
      const snap = await getDocs(collection(db, 'users'));
      const list = snap.docs
        .map(d => ({ uid: d.id, ...d.data() }))
        .filter(u => u.username && u.uid !== currentUser?.uid)
        .map(u => ({ uid: u.uid, username: u.username, displayName: u.displayName || u.username, photoURL: u.photoURL || null }));
      setAllUsers(list);
    } catch { setAllUsers([]); }
  };

  // Add a known registered user (from a name-search suggestion), to the Top 8 if
  // there's room, otherwise to Connections (request sent if not already linked).
  const addRegisteredUser = async (user) => {
    setAddError('');
    if (user.uid === currentUser?.uid) { setAddError("You can't add yourself."); return; }
    if (top8.some(e => e.yt8Username === user.username)) { setStatus(`${user.displayName} is already in your Top 8.`, false); setNewInput(''); return; }
    let connectionStatus = 'pending';
    try {
      const existing = await getConnection(currentUser.uid, user.uid);
      if (existing?.status === 'accepted') connectionStatus = 'accepted';
      else if (!existing) {
        await sendConnect(
          { uid: currentUser.uid, username: currentUserProfile?.username, displayName: currentUserProfile?.displayName, photoURL: currentUserProfile?.photoURL },
          { uid: user.uid, username: user.username, displayName: user.displayName },
        );
      }
    } catch (e) { console.error('Connect failed:', e); }
    const newEntry = {
      id: genId(), displayName: user.displayName || user.username, photoURL: user.photoURL || null,
      isRegisteredUser: true, yt8Username: user.username, primaryAction: 'PROFILE', primaryValue: user.username,
      connections: [{ id: genId(), type: 'PROFILE', value: user.username }], connectionStatus,
    };
    if (top8.length >= 8) {
      setDrawerTab('connections');
      setStatus(connectionStatus === 'accepted'
        ? `${user.displayName} is in your Connections. Drag them into your Top 8 anytime.`
        : `Connection request sent to @${user.username}.`, true);
    } else {
      // Re-check inside the updater so a rapid double-add (e.g. a laggy double tap,
      // which both pass the stale check above) can't create a twin tile.
      setTop8(prev => prev.some(e => e.yt8Username === user.username) ? prev : [...prev, newEntry]);
    }
    setNewInput('');
  };

  const handleAdd = async () => {
    const input = newInput.trim();
    setAddError('');
    if (!input) return;
    // Adding always works now, even when the Top 8 is full: a full Top 8 routes
    // a registered user into Connections and a phone/social into Prospects.

    setAdding(true);
    try {
      if (isPhoneNumber(input)) {
        const phone = normalizePhone(input);
        const id = genId();
        if (top8.some(e => e.connections?.some(c => c.type === 'TEXT' && c.value === phone))) {
          setAddError('That number is already in your Top 8.'); setAdding(false); return;
        }
        setPendingEntry({
          id, displayName: '', photoURL: null, isRegisteredUser: false,
          yt8Username: null, primaryAction: 'TEXT', primaryValue: phone,
          connections: [{ id: genId(), type: 'TEXT', value: phone }],
        });
        setPendingName('');
        setNewInput('');

      } else if (isURL(input)) {
        const raw = input.startsWith('http') ? input : `https://${input}`;
        try { new URL(raw); } catch { setAddError('Invalid URL.'); setAdding(false); return; }
        // Whitelist check against the single source of truth (platforms.js).
        const key = detectPlatform(raw);
        if (!key) {
          setAddError(
            `That site isn't permitted. Supported: ${SUPPORTED_PLATFORM_NAMES.join(', ')}. ` +
            `Think we should add it? Email us at hello@yourtop8.com`
          );
          setAdding(false); return;
        }
        const url = buildSocialUrl(key, raw);
        if (top8.some(e => e.connections?.some(c => c.type === 'SOCIAL' && c.value === url))) {
          setAddError('Already in your Top 8.'); setAdding(false); return;
        }
        const id = genId();
        setPendingEntry({
          id, displayName: '', photoURL: null, isRegisteredUser: false,
          yt8Username: null, primaryAction: 'SOCIAL', primaryValue: url,
          connections: [{ id: genId(), type: 'SOCIAL', value: url }],
        });
        setPendingName('');
        setNewInput('');

      } else {
        // YT8 username lookup
        const username = input.toLowerCase();
        if (username === currentUserProfile?.username) {
          setAddError("You can't add yourself."); setAdding(false); return;
        }
        const usernameSnap = await getDoc(doc(db, 'usernames', username));
        if (!usernameSnap.exists()) {
          setAddError(`No user "${input}" found.`); setAdding(false); return;
        }
        if (top8.some(e => e.yt8Username === username)) {
          setAddError(`${input} is already in your Top 8.`); setAdding(false); return;
        }
        const uid = usernameSnap.data().uid;
        const userSnap = await getDoc(doc(db, 'users', uid));
        const userData = userSnap.data();
        // Reuse the user-to-user connection. If we're already connected, the tile
        // is connected with no new request. If there's a pending request either
        // way, leave it. Only a brand-new pairing sends a request.
        let connectionStatus = 'pending';
        if (uid !== currentUser?.uid) {
          const existing = await getConnection(currentUser.uid, uid);
          if (existing?.status === 'accepted') {
            connectionStatus = 'accepted';
          } else if (!existing) {
            try {
              await sendConnect(
                {
                  uid: currentUser.uid,
                  username: currentUserProfile?.username,
                  displayName: currentUserProfile?.displayName,
                  photoURL: currentUserProfile?.photoURL,
                },
                { uid, username, displayName: userData.displayName }
              );
            } catch (e) {
              console.error('Failed to send connection request:', e);
            }
          }
        }
        const newEntry = {
          id: genId(),
          displayName: userData.displayName || username,
          photoURL: userData.photoURL || null,
          isRegisteredUser: true,
          yt8Username: username,
          primaryAction: 'PROFILE',
          primaryValue: username,
          connections: [{ id: genId(), type: 'PROFILE', value: username }],
          connectionStatus,
        };
        if (top8.length >= 8) {
          // Top 8 is full: they're a real account, so they belong in Connections.
          setDrawerTab('connections');
          if (connectionStatus === 'accepted') {
            setStatus(`${userData.displayName || input} is in your Connections. Drag them into your Top 8 anytime.`, true);
          } else {
            setStatus(`Connection request sent to @${username}.`, true);
          }
        } else {
          // Race-safe: re-check inside the updater so a double-submit can't twin a tile.
          setTop8(prev => prev.some(e => e.yt8Username === username) ? prev : [...prev, newEntry]);
        }
        setNewInput('');
      }
    } catch {
      setAddError('Something went wrong.');
    }
    setAdding(false);
  };

  // ── Pending entry confirm ────────────────────────────────────────────────────

  const handlePendingConfirm = async () => {
    if (!pendingEntry || !pendingName.trim()) return;
    setPendingUploading(true);
    try {
      let photoURL = null;
      if (pendingPhoto) {
        const uid = currentUser.uid;
        const fileRef = storageRef(storage, `top8-photos/${uid}/${Date.now()}`);
        const blob = await downscaleImage(pendingPhoto, 512);
        await uploadBytes(fileRef, blob);
        photoURL = await getDownloadURL(fileRef);
      }
      const tile = { ...pendingEntry, displayName: pendingName.trim(), photoURL };
      if (top8.length >= 8) {
        // Top 8 full: bench them as a Prospect, where they can be invited or
        // dragged into a slot later.
        setProspects(prev => [...prev, tile]);
        setDrawerTab('prospects');
        setStatus('Added to Prospects. Open them to invite, or drag into your Top 8.', true);
      } else {
        setTop8(prev => [...prev, tile]);
      }
      } catch {
      setAddError('Photo upload failed. Try again.');
    }
    setPendingEntry(null);
    setPendingName('');
    setPendingPhoto(null);
    setPendingPhotoPreview(null);
    setPendingUploading(false);
  };

  const handlePendingCancel = () => {
    setPendingEntry(null);
    setPendingName('');
    setPendingPhoto(null);
    setPendingPhotoPreview(null);
  };

  // ── Save ─────────────────────────────────────────────────────────────────────

  const handleConfigSave = (entryId, updates) => {
    const isProspect = prospects.some(p => p.id === entryId);
    if (isProspect) {
      setProspects(prev => prev.map(e => e.id === entryId ? { ...e, ...updates } : e));
    } else {
      setTop8(prev => prev.map(e => e.id === entryId ? { ...e, ...updates } : e));
    }
    setConfigEntry(null);
  };

  const handleConfigRemove = (entryId) => {
    const entry = top8.find(e => e.id === entryId) || prospects.find(e => e.id === entryId);
    setConfigEntry(null);
    if (entry) setConfirmRemove(entry); // confirm before removing
  };

  // Actually remove, after the "are you sure" confirm. Works for a Top 8 tile or
  // a Prospect on the bench.
  const doRemove = () => {
    if (!confirmRemove) return;
    setTop8(prev => prev.filter(e => e.id !== confirmRemove.id));
    setProspects(prev => prev.filter(e => e.id !== confirmRemove.id));
    setStatus('Removed', true);
    setConfirmRemove(null);
  };

  const emptyCount = Math.max(0, 8 - top8.length);

  // Drawer search applies to whichever tab is showing.
  const q = connSearch.trim().toLowerCase();
  const top8UsernameSet = new Set(top8.map(t => t.yt8Username).filter(Boolean));
  const matchesQ = (c) => !q || (c.username || '').toLowerCase().includes(q) || (c.displayName || '').toLowerCase().includes(q);
  const drawerConns = connections
    .filter(c => c.username && !top8UsernameSet.has(c.username))
    .filter(matchesQ);
  const drawerPending = pending
    .filter(c => c.username && !top8UsernameSet.has(c.username))
    .filter(matchesQ);
  const drawerProspects = prospects
    .filter(p => !q || (p.displayName || '').toLowerCase().includes(q));

  // Name search for the add bar: suggest matching YT8 users as you type a name.
  const nameQ = newInput.trim().toLowerCase();
  const showSuggest = nameQ.length >= 2 && !isPhoneNumber(newInput.trim()) && !isURL(newInput.trim());
  const suggestions = showSuggest && Array.isArray(allUsers)
    ? allUsers
        .filter(u => u.username.toLowerCase().includes(nameQ) || u.displayName.toLowerCase().includes(nameQ))
        .filter(u => !top8.some(t => t.yt8Username === u.username))
        .slice(0, 6)
    : [];

  const body = (
    <>
      <div className="grid-card">
        <div className="grid-head">
          <h2>Your Top 8</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <div className="layout-switch" role="group" aria-label="Top 8 layout">
              {LAYOUTS.map(l => (
                <button
                  key={l.key}
                  type="button"
                  className={layout === l.key ? 'active' : ''}
                  onClick={() => changeLayout(l.key)}
                  title={`${l.label} layout`}
                >{l.label}</button>
              ))}
            </div>
            {onDone && <button className="btn btn-sm" onClick={onDone}>Done</button>}
          </div>
        </div>
        <div className="grid-note" style={{ margin: '-2px 0 10px' }}>
          Tap to open. Long-press to rearrange, or hit the pencil to edit. Drag to a red bar to remove.
        </div>

        {conflict && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            padding: '10px 14px', marginBottom: 12, borderRadius: 'var(--radius)',
            background: 'rgba(244,185,66,0.12)', border: '1px solid rgba(244,185,66,0.35)',
            fontSize: 13,
          }}>
            <span>Your Top 8 changed on another device.</span>
            <button className="btn btn-sm" style={{ flexShrink: 0, background: '#f4b942', color: '#2a1800', border: 'none', fontWeight: 700 }} onClick={() => loadTiles()}>
              Reload
            </button>
          </div>
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <DeleteZone side="left" visible={!!activeId && !activeChip} />
          <DeleteZone side="right" visible={!!activeId && !activeChip} />

          <SortableContext items={top8.map(t => t.id)} strategy={rectSortingStrategy}>
            <div className="top8" data-layout={layout}>
              {top8.map((entry, idx) => (
                <SortableTile
                  key={entry.id}
                  entry={entry}
                  rank={idx + 1}
                  en={enrichMap[entry.id]}
                  onOpen={firePrimaryAction}
                  onEdit={setConfigEntry}
                  onInvite={handleInvite}
                  justDraggedRef={justDraggedRef}
                  dropTarget={!!activeChip && overId === entry.id}
                  leaving={!!activeChip && overId != null && !String(overId).startsWith('empty') && top8.length >= 8 && idx === top8.length - 1}
                />
              ))}
              {Array.from({ length: emptyCount }).map((_, i) => (
                <EmptySlot key={`empty-${i}`} id={`empty-${i}`} onFocus={() => inputRef.current?.focus()} />
              ))}
            </div>
          </SortableContext>

          {/* Drawer: Connections + Prospects. Drag into a slot to feature someone. */}
          <div className="conn-drawer">
            <div className="conn-drawer-tabs">
              <button type="button" className={`conn-tab${drawerTab === 'connections' ? ' active' : ''}`} onClick={() => setDrawerTab('connections')}>
                Connections{connections.length ? ` (${drawerConns.length})` : ''}
              </button>
              <button type="button" className={`conn-tab${drawerTab === 'pending' ? ' active' : ''}`} onClick={() => setDrawerTab('pending')}>
                Pending{pending.length ? ` (${drawerPending.length})` : ''}
              </button>
              <button type="button" className={`conn-tab${drawerTab === 'prospects' ? ' active' : ''}`} onClick={() => setDrawerTab('prospects')}>
                Prospects{prospects.length ? ` (${drawerProspects.length})` : ''}
              </button>
              {(connections.length > 0 || pending.length > 0 || prospects.length > 0) && (
                <input
                  className="input conn-search"
                  value={connSearch}
                  onChange={e => setConnSearch(e.target.value)}
                  placeholder="Search"
                />
              )}
            </div>

            {drawerTab === 'connections' ? (
              connections.length === 0 ? (
                <div className="conn-drawer-empty">People you connect with on Your Top 8 show up here. Drag one into a slot to feature them.</div>
              ) : drawerConns.length === 0 ? (
                <div className="conn-drawer-empty">{q ? 'No connections match that.' : 'Everyone you are connected with is already in your Top 8.'}</div>
              ) : (
                <>
                  <div className="conn-drawer-hint">Drag onto a tile to slot them in. A full Top 8 drops its last spot.</div>
                  <div className="conn-strip">
                    {drawerConns.map(c => (
                      <DraggableChip
                        key={c.uid}
                        id={`conn:${c.uid}`}
                        name={c.displayName}
                        photoURL={c.photoURL}
                        title={`Drag ${c.displayName} into your Top 8`}
                        onTap={() => navigate(`/${c.username}`)}
                        justDraggedRef={justDraggedRef}
                      />
                    ))}
                  </div>
                </>
              )
            ) : drawerTab === 'pending' ? (
              pending.length === 0 ? (
                <div className="conn-drawer-empty">Requests you've sent that haven't been accepted yet show up here. You can still drag them into your Top 8.</div>
              ) : drawerPending.length === 0 ? (
                <div className="conn-drawer-empty">{q ? 'No pending requests match that.' : 'Everyone you have a pending request with is already in your Top 8.'}</div>
              ) : (
                <>
                  <div className="conn-drawer-hint">Awaiting their acceptance. Drag onto a tile to feature them now.</div>
                  <div className="conn-strip">
                    {drawerPending.map(c => (
                      <DraggableChip
                        key={c.uid}
                        id={`conn:${c.uid}`}
                        name={c.displayName}
                        photoURL={c.photoURL}
                        title={`Drag ${c.displayName} into your Top 8 (request pending)`}
                        onTap={() => navigate(`/${c.username}`)}
                        justDraggedRef={justDraggedRef}
                      />
                    ))}
                  </div>
                </>
              )
            ) : (
              prospects.length === 0 ? (
                <div className="conn-drawer-empty">Add someone by phone number or a social link, even when your Top 8 is full, and they wait here. Tap a prospect to invite them.</div>
              ) : drawerProspects.length === 0 ? (
                <div className="conn-drawer-empty">No prospects match that.</div>
              ) : (
                <>
                  <div className="conn-drawer-hint">Tap to invite, or drag onto a tile to slot them in.</div>
                  <div className="conn-strip">
                    {drawerProspects.map(p => (
                      <DraggableChip
                        key={p.id}
                        id={`prospect:${p.id}`}
                        name={p.displayName}
                        photoURL={p.photoURL}
                        title={`Drag ${p.displayName} into your Top 8, or tap to invite`}
                        onTap={() => setConfigEntry(p)}
                        justDraggedRef={justDraggedRef}
                      />
                    ))}
                  </div>
                </>
              )
            )}
          </div>

          <DragOverlay>
            {activeEntry
              ? <div className="tile tile--overlay"><TileCard entry={activeEntry} en={enrichMap[activeEntry.id]} overlay /></div>
              : activeChip
                ? (
                    <div className="conn-chip conn-chip--overlay">
                      <div className="conn-chip-avatar">
                        {activeChip.photoURL
                          ? <img src={activeChip.photoURL} alt="" draggable={false} />
                          : (activeChip.displayName?.[0]?.toUpperCase() || '?')}
                      </div>
                      <div className="conn-chip-name">{activeChip.displayName}</div>
                    </div>
                  )
                : null}
          </DragOverlay>
        </DndContext>

        {/* Add bar */}
        <div className={`add-bar${coachAdd ? ' add-bar--coach' : ''}`}>
          <input
            ref={inputRef}
            className="input"
            value={newInput}
            onChange={e => {
              const v = e.target.value;
              setNewInput(v); setAddError('');
              const t = v.trim();
              if (t.length >= 2 && !isPhoneNumber(t) && !isURL(t)) ensureAllUsers();
            }}
            onKeyDown={e => {
              if (e.key !== 'Enter') return;
              if (suggestions.length === 1) addRegisteredUser(suggestions[0]);
              else handleAdd();
            }}
            placeholder="Name, username, phone number, or social link"
            disabled={adding}
          />
          <button
            className="btn btn-primary"
            onClick={handleAdd}
            disabled={!newInput.trim() || adding}
            style={{ flexShrink: 0 }}
          >
            {adding ? <span className="spinner" style={{ width: 16, height: 16, borderTopColor: '#0d0d0d' }} /> : 'Add'}
          </button>
        </div>

        {suggestions.length > 0 && (
          <div className="add-suggest">
            {suggestions.map(u => (
              <button key={u.uid} type="button" className="add-suggest-item" onClick={() => addRegisteredUser(u)}>
                <span className="add-suggest-avatar">
                  {u.photoURL ? <img src={u.photoURL} alt="" /> : (u.displayName?.[0]?.toUpperCase() || '?')}
                </span>
                <span className="add-suggest-text">
                  <span className="add-suggest-name">{u.displayName}</span>
                  <span className="add-suggest-handle">@{u.username}</span>
                </span>
              </button>
            ))}
          </div>
        )}

        {addError && (
          <p style={{ color: 'var(--bad)', fontSize: 13, marginTop: 6, marginBottom: 0 }}>{addError}</p>
        )}

        {top8.length >= 8 && (
          <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: 6, marginBottom: 0 }}>
            Your Top 8 is full. You can still <Link to="/people" style={{ color: 'var(--accent)' }}>connect with more people</Link> anytime. Connections aren't capped.
          </p>
        )}

        <div className="save-row" style={{ alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--muted)', cursor: 'pointer' }} title="Off: rearrange freely, then save once so people get a single notification.">
            <input type="checkbox" checked={autoSave} onChange={toggleAutoSave} style={{ accentColor: 'var(--accent)', cursor: 'pointer' }} />
            Auto-save
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className={`status${saveStatus.good ? ' good' : ''}`}>
              {saving
                ? <span className="spinner" style={{ width: 12, height: 12 }} />
                : (saveStatus.msg || (!autoSave && hasUnsaved ? 'Unsaved changes' : ''))
              }
            </span>
            {!autoSave && (
              <button
                className="btn btn-primary btn-sm"
                onClick={saveNow}
                disabled={saving || !hasUnsaved}
              >
                Save
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Person config modal */}
      {configEntry && (
        <PersonConfigModal
          entry={configEntry}
          onSave={handleConfigSave}
          onRemove={handleConfigRemove}
          onClose={() => setConfigEntry(null)}
          currentUserProfile={currentUserProfile}
          onOpenConnection={openConnection}
        />
      )}

      {/* Confirm before removing a friend */}
      {confirmRemove && (
        <div className="modal-overlay" onClick={() => setConfirmRemove(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 360 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Remove?</h3>
            <p style={{ fontSize: 14, color: 'var(--muted)', margin: '8px 0 20px' }}>
              Remove {confirmRemove.displayName || 'this person'}
              {prospects.some(p => p.id === confirmRemove.id) ? ' from your prospects' : ' from your Top 8'}?
              {' '}You can add them back anytime.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setConfirmRemove(null)}>Cancel</button>
              <button className="btn" style={{ background: 'var(--bad)', color: '#fff', border: 'none' }} onClick={doRemove}>Remove</button>
            </div>
          </div>
        </div>
      )}

      {/* Name & photo modal for social/phone adds */}
      {pendingEntry && (
        <div className="modal-overlay" onClick={handlePendingCancel}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Who is this?</h3>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--muted)' }}>
                {pendingEntry.primaryAction === 'TEXT'
                  ? `Add a name and photo for ${pendingEntry.primaryValue}`
                  : `Add a name and photo for this ${getPlatformLabel(pendingEntry.primaryValue)} link`
                }
              </p>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
              <div
                className="photo-picker-avatar"
                onClick={() => pendingPhotoRef.current?.click()}
                style={{ width: 64, height: 64, borderRadius: 12, fontSize: 28 }}
              >
                {pendingPhotoPreview
                  ? <img src={pendingPhotoPreview} alt="preview" />
                  : <span>📷</span>
                }
              </div>
              <div>
                <button type="button" className="btn btn-sm" onClick={() => pendingPhotoRef.current?.click()}>
                  {pendingPhotoPreview ? 'Change Photo' : 'Add Photo'}
                </button>
                <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 5, marginBottom: 0 }}>Optional</p>
              </div>
              <input
                ref={pendingPhotoRef}
                type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => {
                  const file = e.target.files[0];
                  if (!file) return;
                  setPendingPhoto(file);
                  setPendingPhotoPreview(URL.createObjectURL(file));
                }}
              />
            </div>

            <div className="form-group">
              <label>Name</label>
              <input
                className="input"
                value={pendingName}
                onChange={e => setPendingName(e.target.value)}
                placeholder="e.g. Alex Smith"
                autoFocus
                onKeyDown={e => e.key === 'Enter' && handlePendingConfirm()}
              />
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
              <button className="btn btn-ghost" onClick={handlePendingCancel} style={{ flex: 1 }}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handlePendingConfirm}
                disabled={!pendingName.trim() || pendingUploading}
                style={{ flex: 2 }}
              >
                {pendingUploading
                  ? <span className="spinner" style={{ width: 16, height: 16, borderTopColor: '#0d0d0d' }} />
                  : 'Add to Top 8'
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return embedded ? body : <div className="wrap">{body}</div>;
}

export default Top8Editor;
